import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CardScreen } from './CardScreen';
import { t } from '@/strings/index';
import { useDispatch } from 'react-redux';
import type { PolicyEvaluateResponse } from '@/models/flw-orchestrator.types';
import type { PolicyEvaluateRequest } from '@/models/flw-orchestrator.request';
import { Device, Environment, PolicyEvaluationStep } from '@/models/flw-orchestrator.types';
import { PolicyEvaluationService, type PolicyEvaluationParams, type RetryState } from '@/utils/policyEvaluationService';
import { AppDispatch } from '@/store';
import { logScreenRender, logRetryStep, logButtonClick } from '../../telemetry/telemetryEvents';
import { logDev as debugLog, errorDev } from '@/logging/devConsole';
import { getFeatureFlag } from '@/services/ecs';

/**
 * RetryScreen handles manual retry of MFA or MAM evaluation on button click.
 * Retries up to 3 API calls. After 3 failed attempts, Continue proceeds.
 * Uses consolidated PolicyEvaluationService for all evaluation logic.
 */
const RetryScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const state = (location.state as RetryState) || {};
  const maxAttempts = getFeatureFlag('retryMaxAttempts', 3);
  
  const step: PolicyEvaluationStep = state.step;
  const env = state.env as Environment;
  const userId: string = state.userId;
  const applicationId: string = state.applicationId;
  const devicePlatform = state.devicePlatform;
  const clientAppType: PolicyEvaluateRequest['clientAppType'] = state.clientAppType;
  const region: string = state.region;
  const selectedDevice: Device = state.selectedDevice;
  const effectiveStep = step ?? PolicyEvaluationStep.MFA;
  const initialAttempt = (typeof state.retryCount === 'number' ? state.retryCount : 0) + 1;

  // State for retry count
  const [retryCount, setRetryCount] = useState<number>(() => {
    // Initialize from navigation state on first mount only
    return typeof state.retryCount === 'number' ? state.retryCount : 0;
  });

  // Sync retry count when navigation state changes (e.g., transitioning between steps)
  useEffect(() => {
    if (typeof state.retryCount === 'number') {
      debugLog('RetryScreen: Syncing retryCount from navigation state:', state.retryCount);
      setRetryCount(state.retryCount);
    }
  }, [state.retryCount, state.step]);

  useEffect(() => {
    // logScreenRender handles deviceType automatically via telemetryManager
    logScreenRender('RetryScreen_' + effectiveStep);
    logRetryStep(effectiveStep, initialAttempt);
  }, [effectiveStep, initialAttempt]);

  // Debug logging
  debugLog('RetryScreen render:', { 
    retryCount,
    navigationRetryCount: state.retryCount
  });

  const title = retryCount >= maxAttempts ? t('api.continueErrorTitle') : t('api.errorTitle');
  const buttonText = retryCount >= maxAttempts ? t('api.continueAction') : t('api.retryTitle');
  const subtitle = t('api.retryBody');

  // Build policy params once for reuse
  const policyParams: PolicyEvaluationParams = {
    env,
    userId,
    applicationId,
    devicePlatform,
    clientAppType,
    region,
    selectedDevice
  };

  /**
   * Helper function to navigate to retry screen with given count
   */
  const navigateToRetryScreen = (count: number) => {
    PolicyEvaluationService.navigateToRetry(
      navigate,
      step,
      policyParams,
      state.mfaResult,
      count,
      false,
      state.mamResult
    );
  };
  
  const handlePrimary = async () => {
    logButtonClick('RetryScreen', buttonText + "_" + effectiveStep + "_" + retryCount);

    try {
      if (retryCount < maxAttempts) {
        // Increment the retry count immediately on button click
        const nextRetryCount = retryCount + 1;
        debugLog('Retry button clicked, current count:', retryCount, 'incrementing to:', nextRetryCount);
        
        // Update local state immediately
        setRetryCount(nextRetryCount);
        
        // Handle retry - service will navigate to loading screen, then to next step or back to retry
        await PolicyEvaluationService.handleRetry(
          dispatch,
          navigate,
          policyParams,
          step,
          nextRetryCount,
          state.mfaResult
        );
        // Note: Navigation is handled by the service, so we don't need to do anything here
      } else {
        // After 3 failed attempts, Continue proceeds
        await PolicyEvaluationService.handleContinueAfterFailures(
          dispatch,
          navigate,
          policyParams,
          step,
          state.mfaResult
        );
      }
    } catch (error) {
      errorDev('Retry/Continue error:', error);
      const nextRetryCount = retryCount + 1;
      debugLog('Exception occurred, navigating with count:', nextRetryCount);
      // Note: Error is already logged by PolicyEvaluationService.handleRetry
      // Navigate to retry screen with the incremented count
      navigateToRetryScreen(nextRetryCount);
    }
  };

  return (
    <CardScreen
      title={title}
      subtitle={subtitle}
      primaryButtonText={buttonText}
      onPrimaryButtonClick={handlePrimary}
      logoAltText="Tenant logo"
    />
  );
};

export default RetryScreen;